import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import {
  IconButton,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  DialogActions,
  Button,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { RHFAutocomplete, RHFTextField } from '../hook-form';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from '../hook-form/form-provider';
import { useSearchParams } from 'src/routes/hooks';
import { handleErrorMessages } from '../risk-management/risk-register/feching';
import { postRequest } from 'src/services';
import { endpoints } from 'src/utils/axios';

const DropDownAddValueCustom = ({
  name,
  label,
  options = [],
  url,
  reload,
  cat,
  getSubCat,
  setCatId,
  subCatReload,
}) => {
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const searchParams = useSearchParams();
  const token = searchParams.get('token') || localStorage.getItem('authToken') || '';

  const NewUserSchema = Yup.object().shape({
    label: Yup.string().required(`${label} is required`),
  });

  // Default form values
  const defaultValues = useMemo(
    () => ({
      label: '',
    }),
    []
  );

  // Hook form setup with validation schema
  const methods = useForm({
    resolver: yupResolver(NewUserSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = methods;

  useEffect(() => {
    if (cat && getSubCat) {
      const foundItem = options?.find((item) => item?.name === cat.name);
      if (foundItem?.id) {
        setCatId(foundItem?.id);
        getSubCat(
          `${endpoints.accounting.expenses.get_sub_category_list}/${foundItem.id}/subcategories`
        );
      }
    }
  }, [cat, subCatReload]);

  const handleOpen = (e) => {
    e.stopPropagation(); // Prevent the parent form from submitting
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    reset(); // Reset form when modal is closed
  };

  const validateForm = () => {
    const formData = watch();
    if (!formData.label) {
      enqueueSnackbar(`${label} is required`, { variant: 'error' });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    setLoading(true);
    if (!validateForm()) return;
    const formData = new FormData();
    formData.append('name', watch('label'));
    try {
      const response = await postRequest(`${url}`, formData, true);
      if (response.metadata?.success) {
        reload();
        reset();
        handleClose();
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'start' }}>
        {/* <RHFAutocomplete
          sx={{ width: '100%' }}
          name={name}
          disabled={!cat && name === 'expense_subcategory_id' ? 'true' : false}
          label={label} // Use plain label here
          options={options.map((option) => option.name)}
          getOptionLabel={(option) => option}
          isOptionEqualToValue={(option, value) => option === value}
          renderOption={(props, option) => {
            const { name, id } = options.find((item) => item.name === option) || {};
            return (
              <li {...props} key={id}>
                <Iconify width={28} sx={{ mr: 1 }} />
                {name}
              </li>
            );
          }}
        /> */}
        <RHFAutocomplete
          sx={{ width: '100%' }}
          name={name}
          disabled={!cat && name === 'expense_subcategory_id'}
          label={label}
          options={options} // Pass full objects instead of just names
          getOptionLabel={(option) => option?.name || ''} // Display the name
          isOptionEqualToValue={(option, value) => option?.id === value?.id} // Compare by ID
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Iconify width={28} sx={{ mr: 1 }} />
              {option.name}
            </li>
          )}
        />
        {(name !== 'expense_subcategory_id' || cat) && (
          <IconButton
            onClick={handleOpen}
            className="cardColor"
            sx={{
              margin: '5px 10px',

              color: '#FFF',
              fontSize: 25,
              padding: '7px',
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: '#7AB13A',
              },
              boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Iconify icon="mingcute:add-line" />
          </IconButton>
        )}
      </Box>

      {/* Modal with Error Handling */}
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <FormProvider methods={methods}>
          <DialogTitle>Add New {label}</DialogTitle>
          <Divider />
          <DialogContent>
            <Box
              mt={2}
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns="repeat(1, 1fr)"
            >
              <RHFTextField
                name="label"
                label={`${label} *`} // Asterisk for required field in modal
                error={!!errors.label} // Show error if it exists
                helperText={errors.label?.message} // Error message for validation
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="inherit" onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              className="dark-button"
              disabled={loading || isSubmitting}
              loading={loading || isSubmitting}
              variant="contained"
              onClick={handleSubmit(handleSave)} // Custom save handler
            >
              Save
            </LoadingButton>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
};

export default DropDownAddValueCustom;
